import {styled} from '@linaria/react';
import {
  useRive,
  useStateMachineInput,
  useViewModelInstanceColor,
} from '@rive-app/react-canvas';
import React, {useDeferredValue, useEffect, useState} from 'react';
import {COLORS} from '../../../../constants/colors';
import bellNotificationRivBuffer from '../assets/bell-notification.riv?buffer';
import {hexToRiveColorNumber} from '../utils/hexToRiveColorNumber';
import {BellNotificationFallback} from './BellNotificationFallback';

const stateMachineName = 'State Machine 1';
const inputName = 'has new notification';

export const BellNotification: React.FC<{
  color?: 'white' | 'default';
  hasNewNotification: boolean;
  ref?: React.Ref<HTMLDivElement | null>;
}> = ({color = 'default', hasNewNotification, ref}) => {
  const [loaded, setLoaded] = useState(false);
  const deferredLoaded = useDeferredValue(loaded, false);

  const {rive, RiveComponent} = useRive(
    {
      buffer: bellNotificationRivBuffer,
      stateMachines: stateMachineName,
      autoplay: true,
      autoBind: true,
      onRiveReady: () => {
        requestAnimationFrame(() => {
          setLoaded(true);
        });
      },
    },
    {
      useDevicePixelRatio: true,
    },
  );

  const hasNewNotificationInput = useStateMachineInput(
    rive,
    stateMachineName,
    inputName,
    hasNewNotification,
  );

  const viewModelInstance = rive?.viewModelInstance;

  const {setValue: setDotColor} = useViewModelInstanceColor(
    'dot',
    viewModelInstance,
  );

  const {setValue: setBellColor} = useViewModelInstanceColor(
    'bell',
    viewModelInstance,
  );

  useEffect(() => {
    if (viewModelInstance) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setBellColor(
        hexToRiveColorNumber(
          color === 'white' ? COLORS['Fill.White'] : COLORS['Fill.Black'],
        ),
      );

      setDotColor(
        hexToRiveColorNumber(
          color === 'white'
            ? COLORS['Fill.White']
            : COLORS['Alerts.Error.Primary'],
        ),
      );
    }
  }, [color, setBellColor, setDotColor, viewModelInstance]);

  useEffect(() => {
    if (hasNewNotificationInput) {
      hasNewNotificationInput.value = hasNewNotification;
    }
  }, [hasNewNotification, hasNewNotificationInput]);

  return (
    <Container ref={ref}>
      <StyledBellNotificationFallback $show={!deferredLoaded} />
      <RiveComponent />
    </Container>
  );
};

const StyledBellNotificationFallback = styled(BellNotificationFallback)<{
  $show?: boolean;
}>`
  position: absolute;
  inset: 0;
  display: ${props => (props.$show ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin-inline: -5px;
`;
